import { useEffect, useRef, useState } from 'react';
import {
  RUNTIME_TO_TOOL_ID,
  TOOL_ID_TO_RUNTIME,
  canActivateTool,
  getShapeFlyoutDefs,
  getToolStripDefs,
  getTypeFlyoutDefs,
  isShapeRuntimeTool,
  isTypeRuntimeTool,
  type RuntimeCanvasTool,
  type ShapeRuntimeTool,
  type TypeRuntimeTool,
} from '@canvas/shared-types';
import { useCanvasStore, type CanvasTool } from '@/stores/canvasStore';
import { TOOL_ICONS } from '@/lib/toolIcons';

const STRIP_TOOLS = getToolStripDefs();
const SHAPE_TOOLS = getShapeFlyoutDefs().filter(canActivateTool);
const TYPE_TOOLS = getTypeFlyoutDefs().filter(canActivateTool);

type FlyoutKind = 'shape' | 'type' | null;

export default function ToolStrip() {
  const { selectedTool, lastShapeTool, lastTypeTool, setTool, undo, redo } =
    useCanvasStore();
  const [flyout, setFlyout] = useState<FlyoutKind>(null);
  const stripRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!flyout) return;
    const onDoc = (e: MouseEvent) => {
      if (!stripRef.current?.contains(e.target as Node)) setFlyout(null);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setFlyout(null);
    };
    document.addEventListener('mousedown', onDoc);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDoc);
      window.removeEventListener('keydown', onKey);
    };
  }, [flyout]);

  const shapeActive = isShapeRuntimeTool(selectedTool);
  const typeActive = isTypeRuntimeTool(selectedTool);
  const activeShapeId = RUNTIME_TO_TOOL_ID[shapeActive ? selectedTool : lastShapeTool];
  const activeTypeId = RUNTIME_TO_TOOL_ID[typeActive ? selectedTool : lastTypeTool];

  return (
    <aside className="ps-toolstrip" aria-label="Tools" ref={stripRef}>
      {STRIP_TOOLS.map((def) => {
        if (def.id === 'shape-rect') {
          return (
            <div key="shape-flyout" className="ps-shape-flyout-wrap">
              <button
                type="button"
                className={`ps-tool ${shapeActive ? 'active' : ''}`}
                title="Shapes (U) — click for more"
                aria-label="Shape tools"
                aria-haspopup="menu"
                aria-expanded={flyout === 'shape'}
                aria-pressed={shapeActive}
                onClick={() => {
                  if (shapeActive) setFlyout((f) => (f === 'shape' ? null : 'shape'));
                  else {
                    setTool(lastShapeTool);
                    setFlyout('shape');
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setFlyout('shape');
                }}
              >
                {TOOL_ICONS[activeShapeId] ?? TOOL_ICONS['shape-rect']}
                <span className="ps-flyout-caret" aria-hidden />
              </button>
              {flyout === 'shape' && (
                <div className="ps-shape-flyout" role="menu" aria-label="Shape tools">
                  {SHAPE_TOOLS.map((shape) => {
                    const runtime = TOOL_ID_TO_RUNTIME[shape.id] as ShapeRuntimeTool;
                    return (
                      <button
                        key={shape.id}
                        type="button"
                        role="menuitem"
                        className={`ps-tool ${selectedTool === runtime ? 'active' : ''}`}
                        title={shape.label}
                        onClick={() => {
                          setTool(runtime);
                          setFlyout(null);
                        }}
                      >
                        {TOOL_ICONS[shape.id]}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        if (def.id === 'type-point') {
          return (
            <div key="type-flyout" className="ps-shape-flyout-wrap">
              <button
                type="button"
                className={`ps-tool ${typeActive ? 'active' : ''}`}
                title="Type (T) — click for more"
                aria-label="Type tools"
                aria-haspopup="menu"
                aria-expanded={flyout === 'type'}
                aria-pressed={typeActive}
                onClick={() => {
                  if (typeActive) setFlyout((f) => (f === 'type' ? null : 'type'));
                  else {
                    setTool(lastTypeTool);
                    setFlyout('type');
                  }
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setFlyout('type');
                }}
              >
                {TOOL_ICONS[activeTypeId] ?? TOOL_ICONS['type-point']}
                <span className="ps-flyout-caret" aria-hidden />
              </button>
              {flyout === 'type' && (
                <div className="ps-shape-flyout" role="menu" aria-label="Type tools">
                  {TYPE_TOOLS.map((typeDef) => {
                    const runtime = TOOL_ID_TO_RUNTIME[typeDef.id] as TypeRuntimeTool;
                    return (
                      <button
                        key={typeDef.id}
                        type="button"
                        role="menuitem"
                        className={`ps-tool ${selectedTool === runtime ? 'active' : ''}`}
                        title={typeDef.label}
                        onClick={() => {
                          setTool(runtime);
                          setFlyout(null);
                        }}
                      >
                        {TOOL_ICONS[typeDef.id]}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        }

        const runtime = TOOL_ID_TO_RUNTIME[def.id] as CanvasTool | undefined;
        const active = Boolean(runtime && selectedTool === runtime);
        const enabled = canActivateTool(def) && Boolean(runtime);
        const shortcutHint = def.shortcut ? ` (${def.shortcut})` : '';
        const title = enabled
          ? `${def.label}${shortcutHint}`
          : `${def.label}${shortcutHint} — Coming soon`;

        return (
          <button
            key={def.id}
            type="button"
            className={`ps-tool ${active ? 'active' : ''}`}
            title={title}
            aria-label={title}
            aria-pressed={active}
            disabled={!enabled}
            onClick={() => {
              if (enabled && runtime) {
                setFlyout(null);
                setTool(runtime);
              }
            }}
          >
            {TOOL_ICONS[def.id] ?? <span>{def.shortcut || def.label[0]}</span>}
          </button>
        );
      })}

      <div className="ps-toolstrip-gap" />

      <button
        type="button"
        className="ps-tool"
        title="Undo (Ctrl+Z)"
        aria-label="Undo"
        onClick={() => undo()}
      >
        <svg viewBox="0 0 24 24">
          <path d="M9 7H4v5M4 7c3 0 7 1 9 5 1 2 1 5 0 7" />
        </svg>
      </button>
      <button
        type="button"
        className="ps-tool"
        title="Redo (Ctrl+Y)"
        aria-label="Redo"
        onClick={() => redo()}
      >
        <svg viewBox="0 0 24 24">
          <path d="M15 7h5v5M20 7c-3 0-7 1-9 5-1 2-1 5 0 7" />
        </svg>
      </button>
    </aside>
  );
}

export const TOOL_SHORTCUTS: Record<string, RuntimeCanvasTool> = {
  v: 'select',
  // m handled separately (cycles marquee rect/ellipse)
  l: 'lasso',
  b: 'draw',
  n: 'pencil',
  e: 'eraser',
  p: 'pen',
  h: 'hand',
  o: 'circle',
  z: 'zoom',
  f: 'frame',
  g: 'gradient',
  k: 'bucket',
  i: 'eyedropper',
  c: 'crop',
};

export function nextShapeTool(current: ShapeRuntimeTool): ShapeRuntimeTool {
  const order = SHAPE_TOOLS.map(
    (d) => TOOL_ID_TO_RUNTIME[d.id] as ShapeRuntimeTool
  ).filter(Boolean);
  const idx = order.indexOf(current);
  return order[(idx + 1) % order.length] || 'rectangle';
}

export function nextTypeTool(current: TypeRuntimeTool): TypeRuntimeTool {
  const order = TYPE_TOOLS.map(
    (d) => TOOL_ID_TO_RUNTIME[d.id] as TypeRuntimeTool
  ).filter(Boolean);
  const idx = order.indexOf(current);
  return order[(idx + 1) % order.length] || 'text';
}
